import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import { RegionDto } from '@vatos-pas/common';
import { getSupervisors, UserInterface } from 'services/regions';

export interface RegionFormValues {
  name: string;
  phaseStructureType: string;
  // Drywall fields
  dwHangingCostPerBoard?: number | null;
  dwFinishingCostPerBoard?: number | null;
  dwSprayingCostPerBoard?: number | null;
  // DrywallAndPaint fields
  dwpHangingCostPerBoard?: number | null;
  dwpFinishCostPerBoard?: number | null;
  dwpPrimeCostPerSqft?: number | null;
  dwpTrim1CostPerSqft?: number | null;
  dwpBump1CostPerBoard?: number | null;
  dwpInteriorPaintCostPerSqft?: number | null;
  dwpBump2CostPerBoard?: number | null;
  dwpTrim2CostPerSqft?: number | null;
  dwpFinalPaintCostPerSqft?: number | null;
  dwpBump3CostPerBoard?: number | null;
  dwpCMBluetapeCostPerSqft?: number | null;
  dwpFinalBluetapeCostPerSqft?: number | null;
  exteriorOnePercentage?: number | null;
  exteriorTwoPercentage?: number | null;
  // Common fields
  repairsRate?: number | null;
  pieceworkPercentage?: number | null;
  specialJobsPercentage?: number | null;
  garageCostFixed?: number | null;
  highCeilingPerBoard?: number | null;
  screwInspectionPerBoard?: number | null;
  interiorOptionsPercentage?: number | null;
  exteriorOptionsPercentage?: number | null;
  trimOptionPercentage?: number | null;
  allowPayoutAmountEditing: boolean;
}

/**
 * Region Container - Data Access Layer
 *
 * SWR BEHAVIOR: Uses react-admin's useGetOne which caches data in Redux store.
 * Cached data is returned immediately while fresh data is fetched in the
 * background. Loading spinner only shows on first load when no cached data
 * exists. Cache persists across navigations via Redux.
 */
function useRegion() {
  const dataProvider = useDataProvider();

  const [regionId, setRegionId] = useState<string>('');
  const {
    data: selectedRegion,
    loading: regionLoading,
    loaded: regionLoaded,
    error: regionError,
  } = useGetOne<RegionDto>('region', regionId, { enabled: !!regionId });

  const isLoading = !!regionId && !selectedRegion && regionLoading;
  const isRevalidating = !!regionId && regionLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [supervisors, setSupervisors] = useState<UserInterface[]>([]);
  const [supervisorsLoading, setSupervisorsLoading] = useState(false);

  const loadRegionById = useCallback((id: string): void => {
    setRegionId(id);
  }, []);

  /**
   * One request: the API seeds the default H/O service time windows inside
   * the same transaction that creates the region (see RegionService.createOne
   * and DEFAULT_HO_SERVICE_TIME_WINDOWS in @vatos-pas/common).
   */
  const createRegion = useCallback(
    async (data: RegionFormValues): Promise<RegionDto> => {
      setFormLoading(true);
      try {
        const result = await dataProvider.create<RegionDto>('region', { data });
        const region = result.data;
        setRegionId(region.id);
        return region;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider],
  );

  const updateRegion = useCallback(
    async (id: string, data: Partial<RegionFormValues>): Promise<RegionDto> => {
      if (!selectedRegion) {
        throw new Error('Cannot update: region data not loaded');
      }
      setFormLoading(true);
      try {
        const result = await dataProvider.update<RegionDto>('region', {
          id,
          data: { ...selectedRegion, ...data },
          previousData: selectedRegion,
        });
        return result.data;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider, selectedRegion],
  );

  const loadSupervisors = useCallback(
    async (regionId: string): Promise<void> => {
      setSupervisorsLoading(true);
      try {
        const response = await getSupervisors({ regionId });
        setSupervisors(response.data.data);
      } catch (error) {
        console.error('Failed to load supervisors:', error);
        setSupervisors([]);
      } finally {
        setSupervisorsLoading(false);
      }
    },
    [],
  );

  return {
    // Primary region state (SWR pattern via useGetOne + Redux)
    selectedRegion,
    isLoading, // true only on first load (no cache in Redux)
    isRevalidating, // true during any fetch
    regionLoaded, // true once fetch completed (use to detect "not found")
    regionError, // truthy on fetch failure (404, network, etc.)
    // Secondary state
    formLoading,
    supervisors,
    supervisorsLoading,
    // Actions
    loadRegionById,
    createRegion,
    updateRegion,
    loadSupervisors,
  };
}

export const RegionContainer = createContainer(useRegion);
